import * as React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cva } from 'class-variance-authority';
import PasswordStrengthIndicator from 'components/PasswordStrengthIndicator';
import { usePasswordStrength } from '@poinz/hooks';

interface Props {
  children: React.ReactNode;
  showPasswordStrength?: boolean;
}

const icons = cva('absolute transition-all', {
  variants: {
    is1PasswordBtnVisible: {
      true: 'right-16',
      false: 'right-3'
    },
    showLabel: {
      true: 'top-11',
      false: 'top-3'
    }
  },
  defaultVariants: {
    is1PasswordBtnVisible: false,
    showLabel: false
  }
});

const PasswordInput: React.FC<Props> = ({
  children: InputComponent,
  showPasswordStrength = false
}) => {
  const [passwordVisible, setPasswordVisible] = React.useState(false);
  const [is1PasswordBtnVisible, setIs1PasswordBtnVisible] = React.useState(false);
  const { passwordStrength, handlePasswordChange } = usePasswordStrength();
  const showLabelRef = React.useRef<boolean>(false);
  const check1PasswordBtn = () => {
    // check is there 1 password element in the DOM, it's visible only on input focus
    const onePasswordButtons = document.getElementsByTagName('com-1password-button');

    setIs1PasswordBtnVisible(!!onePasswordButtons.length);
  };

  const handleBlur = () => {
    // there is a race condition between onBlur and clicking "show password" icons
    // put setHas1Password in event loop so that everytings works as expected
    setTimeout(() => setIs1PasswordBtnVisible(false), 100);
  };

  const NewInputComponent = React.Children.map(InputComponent, child => {
    showLabelRef.current = (child as any)?.props?.showLabel;
    return React.cloneElement(child as any, {
      type: passwordVisible ? 'text' : 'password',
      onBlur: handleBlur,
      onClick: check1PasswordBtn,
      ...(showPasswordStrength && { handlePasswordChange })
    });
  });

  return (
    <div className="flex flex-col gap-y-2">
      <div className="PasswordInput relative flex">
        {NewInputComponent}
        <div className={icons({ is1PasswordBtnVisible, showLabel: showLabelRef.current })}>
          {passwordVisible ? (
            <EyeOff
              onClick={() => setPasswordVisible(false)}
              className="cursor-pointer"
              strokeWidth={1.5}
            />
          ) : (
            <Eye
              className="cursor-pointer"
              onClick={() => setPasswordVisible(true)}
              strokeWidth={1.5}
            />
          )}
        </div>
      </div>
      {showPasswordStrength && <PasswordStrengthIndicator strength={passwordStrength} />}
    </div>
  );
};

export default PasswordInput;
